<template>
  <Wrap>
    <div
      :id="String(mainArticles.id)"
      ref="mainArticleRef"
      class="main-article"
      :class="{ 'main-article--loading': isLoading }"
      :data-index="0"
    >
      <SkeletonItem
        v-if="isLoading"
        :style="{
          height: '810px',
        }"
        :animated="true"
      />
      <template v-else>
        <AppLink :to="goToArticle(mainArticles)">
          <div
            v-if="isUgc"
            class="articles-list-reader-experience"
          >
            Опыт читателя
          </div>
          <img
            class="main-article__img"
            :src="mainArticlePhotoUrl"
            @click="useBlock(0, mainArticles)"
          />

          <div class="main-article__content">
            <h3
              class="main-article__content-title"
              @click="useBlock(0, mainArticles)"
              v-html="mainArticles.title"
            />
            <div class="main-article__content-subtitle">
              <div class="articles__time">
                <div class="articles__reading-time">
                  <span class="articles__reading-time-icon">
                    <IconBase
                      name="icon-clock"
                      color="--alias-color-icon-inverse"
                    />
                  </span>
                  <span
                    class="articles__reading-time-text"
                    v-html="minutesStr(mainArticles.readingTime)"
                  />
                </div>
                <span class="dot">·</span>
                <time class="articles__time-date">{{ getDate(mainArticles.publishedAt) }}</time>
              </div>
              <div class="articles__hashtags">
                <AppLink
                  v-for="hashtag in hashtags(mainArticles.geo)"
                  :key="hashtag.slug"
                  class="articles__hashtags-el"
                  :to="goTo(hashtag.slug)"
                  >#{{ hashtag.name }}
                </AppLink>
                <AppLink
                  v-if="isUgc"
                  to="/journal/ugc/"
                  class="articles__hashtags-el"
                >
                  #Опыт читателя
                </AppLink>
              </div>
            </div>
          </div>
        </AppLink>
      </template>
    </div>

    <div class="articles-list">
      <template v-if="isLoading">
        <template v-if="isMobile">
          <JournalCardSkeletonMobile
            v-for="card in 4"
            :key="card"
          />
        </template>
        <template v-else>
          <JournalCardSkeleton
            v-for="card in 4"
            :key="card"
            :image-height="journalArticlesGridCardSkeletonImageHeight"
          />
        </template>
      </template>
      <template v-else>
        <JournalCard
          v-for="(article, i) in articlesList"
          :key="article.id"
          ref="nodeList"
          :index="i + 1"
          :el="article"
          :router-to="goToArticle(article)"
          :use-block="useBlock"
          class="card"
        />
      </template>
    </div>
  </Wrap>
</template>

<script lang="ts" setup>
  import { SkeletonItem } from '@experience/core-skeleton-lib';
  import { computed, onMounted, ref, watch } from 'vue';

  import { trackEvent } from '@/services/analytics';
  import JournalCard from '@/pages/journal/shared/ui/JournalCard.vue';
  import JournalCardSkeleton from '@/pages/journal/shared/ui/JournalCardSkeleton.vue';
  import JournalCardSkeletonMobile from '@/pages/journal/shared/ui/JournalCardSkeletonMobile.vue';
  import { minutesStr, getDate } from '@/shared/helpers/date';
  import type { Article, Geo } from '@/shared/models/journal';
  import { useJournalStore } from '@/shared/stores/journal';
  import { IconBase } from '@/shared/ui/iconBase';
  import { observer } from '@/shared/utils/analytics';
  import { useScreenSize } from '@/shared/utils/breakpoints';

  import Wrap from '@/shared/Wrap.vue';

  const journalStore = useJournalStore();
  const isLoading = computed(() => journalStore.isLoadingArticles);
  const popularArticles = ref<Article[]>([]);
  const mainArticleRef = ref<HTMLElement | null>(null);
  const nodeList = ref([]);
  const { isMobile, isTablet } = useScreenSize();

  const journalArticlesGridCardSkeletonImageHeight = computed(() => {
    if (isTablet.value) {
      return '164px';
    }

    return '287px';
  });

  onMounted(() => soguInitObservers());

  const goToArticle = (el: Article) => `/journal/articles/${el.slug}/`;

  watch(
    () => journalStore.articles,
    async (value) => {
      popularArticles.value = value;
    },
    { deep: true, immediate: true },
  );

  const mainArticles = computed<Article>(() => {
    const slicedArr = { ...popularArticles?.value?.slice(0, 1) };
    return slicedArr?.[0] || {};
  });

  const articlesList = computed<Article[]>(() => {
    return popularArticles?.value?.slice(1, 5);
  });

  const mainArticlePhotoUrl = computed<string>(() => {
    const article = mainArticles.value;
    return article?.mainPhoto?.scaled || article?.mainPhoto?.lightbox || article?.scaledPhotoUrl || '';
  });

  const isUgc = computed<boolean>(() => {
    return mainArticles.value?.isUgc;
  });

  const hashtags = (geo?: Geo) => geo?.country?.slice(0, 3) || [];

  const goTo = (slug: string) => `/journal/country/articles/${slug}/`;

  const soguInitObservers = () => {
    const node = mainArticleRef.value;
    const nodeCardsList = document.getElementsByClassName('card');

    if (node) {
      soguArticlesView(node);
    }

    Array.prototype.forEach.call(nodeCardsList, (el: Element) => {
      if (el instanceof HTMLElement) {
        const i = Number(el.dataset.index);

        if (i <= nodeCardsList.length) {
          soguArticlesView(el);
        }
      }
    });
  };

  const soguArticlesView = async (node: HTMLElement) => {
    observer(node, async () => {
      trackEvent('journal:article.view', {
        block: 'recent',
        position: Number(node.dataset.index),
        article_id: Number(node.id),
      });
    });
  };

  const soguArticleClick = (i: number, articles: Article) => {
    trackEvent('journal:article.click', {
      block: 'recent',
      position: Number(i),
      article_id: Number(articles.id),
    });
  };

  const useBlock = (i: number, articles: Article) => {
    soguArticleClick(i, articles);
  };
</script>

<style lang="scss" scoped>
  .main-article {
    position: relative;
    overflow: hidden;
    width: 100%;
    height: 580px;
    margin-top: 48px;
    border-radius: 16px;
    transition: transform 0.7s ease;

    &:not(.main-article--loading):after {
      content: '';
      position: absolute;
      right: 0;
      bottom: 0;
      left: 0;
      height: 200px;
      background: linear-gradient(180deg, transparent 0%, var(--global-color-transparent-black-a600) 100%);
      pointer-events: none;
    }

    @include tablet {
      height: 412px;
      margin-top: 24px;
    }

    @include mobile {
      margin-top: 16px;
    }
  }

  .main-article:hover img {
    transform: scale(1.1);
  }

  .main-article__content {
    position: absolute;
    bottom: 48px;
    left: 50%;
    z-index: 1;
    width: 100%;
    max-width: 808px;
    color: var(--alias-color-text-inverse);
    transform: translate(-50%);

    @include tablet {
      bottom: 32px;
      max-width: 600px;
    }

    @include mobile {
      bottom: 14px;
      max-width: 288px;
    }
  }

  .main-article__content-subtitle {
    display: flex;
    flex-wrap: wrap;
    gap: 8px 24px;
    justify-content: center;
    align-items: center;
    width: 100%;
    max-width: 800px;
    margin: 8px auto;

    @include tablet {
      flex-direction: column;
      max-width: 600px;
    }
  }

  .main-article__content-title {
    padding: 0 16px;
    font-weight: 800;
    font-size: 32px;
    line-height: 40px;
    text-align: center;
    cursor: pointer;

    @include tablet {
      font-size: 20px;
      line-height: 26px;
    }
  }

  .articles-list-subtitle {
    margin-left: 4px;
  }

  .main-article__img {
    object-fit: cover;
    width: 100%;
    height: 100%;
    border-radius: 16px;
    cursor: pointer;
    transition: transform 0.7s ease;
  }

  .articles__reading-time {
    display: flex;
    gap: 0 4px;
    color: var(--alias-color-text-inverse);
  }

  .articles__reading-time-icon {
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .articles__reading-time-icon .icon {
    fill: var(--alias-color-icon-inverse);
  }

  .articles__reading-time-text {
    display: flex;
    align-items: center;
    font-size: 14px;
  }

  .articles__time {
    display: flex;
  }

  .dot {
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 0 4px;
    color: var(--alias-color-text-inverse);
  }

  .articles__time-date {
    display: flex;
    justify-content: center;
    align-items: center;
    font-size: 14px;
  }

  .articles__hashtags {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
  }

  .articles__hashtags-el {
    color: var(--alias-color-text-inverse);
  }

  .articles-list__hashtags-el {
    color: var(--alias-color-link-default);

    &:hover {
      color: var(--alias-color-link-default-hovered);
    }
  }

  .articles__hashtags-el,
  .articles-list__hashtags-el {
    margin-right: 8px;
    font-size: 14px;
    text-decoration: none;
  }

  .articles-list {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 24px;
    margin-top: 32px;

    @include tablet {
      grid-template-columns: repeat(4, calc((100vw / 4) - 28px));
    }

    @include mobile {
      grid-template-columns: 1fr;
      margin-top: 24px;
    }
  }

  .articles-list__el {
    cursor: pointer;

    @include mobile {
      display: flex;
      flex-direction: row-reverse;
      width: 100%;
    }
  }

  .articles-list-img-container:hover img {
    transform: scale(1.1);
  }

  .articles-list-img-container {
    overflow: hidden;
    width: 100%;
    height: 287px;
    border-radius: 16px;

    @include tablet {
      height: 164px;
    }

    @include mobile {
      min-width: 72px;
      max-width: 72px;
      height: 96px;
      margin-left: 16px;
      border-radius: 8px;
    }
  }

  .articles-list-img {
    object-fit: cover;
    width: 100%;
    height: 100%;
    transition: transform 0.7s ease;
  }

  .articles-list-content {
    margin-top: 16px;
    font-size: 17px;
    line-height: 24px;

    @include tablet {
      margin-top: 8px;
    }

    @include mobile {
      margin-top: 0;
    }
  }

  .articles-list-hashtags {
    display: flex;
    flex-wrap: wrap;
    margin-top: 8px;
    line-height: 24px;

    @include mobile {
      margin-top: 4px;
    }
  }

  .articles-list-hashtags-el {
    margin-right: 8px;
    color: var(--alias-color-link-default);
    font-weight: 400;
    font-size: 14px;
    text-decoration: none;

    &:hover {
      color: var(--alias-color-icon-default);
    }
  }

  .articles-list-dot {
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 0 4px;
    color: var(--alias-color-text-subtle);
    font-size: 14px;
  }

  .articles-list-title {
    margin-top: 16px;
    font-weight: 800;
  }

  .articles-list-time {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    margin-top: 8px;
    color: var(--alias-color-text-subtle);
    font-size: 14px;

    @include mobile {
      margin-top: 4px;
    }
  }

  .articles-list-time__reading-time {
    display: flex;
    gap: 0 4px;
  }

  .articles-list-time__reading-time-icon {
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .articles-list-time__reading-time-icon .icon {
    fill: var(--alias-color-icon-subtle);
  }

  .articles-list-time__reading-time-text {
    display: flex;
    align-items: center;
  }

  .articles-list-reader-experience {
    position: absolute;
    top: 0;
    left: 0;
    z-index: 1;
    padding: 8px 24px;
    border-radius: 16px 0;
    background-color: var(--alias-color-background-accent-orange);
    color: var(--alias-color-text-inverse);

    @include base-300-300-semi;

    @include tablet {
      padding: 6px 12px;

      @include base-200-200-medium;
    }
  }

  .skeletons {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 24px;
    margin-top: 32px;

    @include tablet {
      grid-template-columns: repeat(4, calc((100vw / 4) - 28px));
    }

    @include mobile {
      grid-template-columns: 1fr;
      margin-top: 24px;
    }
  }
</style>
